import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AppNotification, NotificationCategory, SystemErrorDetails } from '../types/notification';

const MAX_NOTIFICATIONS = 50;

interface NotificationState {
  /** Notifications keyed by userId → list (newest first) */
  byUser: Record<string, AppNotification[]>;

  actions: {
    addNotification: (
      userId: string,
      payload: {
        title?: string;
        description?: string;
        link?: string;
        category: NotificationCategory;
        isSystemError?: boolean;
        systemErrorDetails?: SystemErrorDetails;
      }
    ) => void;
    markAsRead: (userId: string, notificationId: string) => void;
    markAllAsRead: (userId: string) => void;
    removeNotification: (userId: string, notificationId: string) => void;
    clearAll: (userId: string) => void;
  };
}

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set) => ({
      byUser: {},

      actions: {
        addNotification: (userId, payload) =>
          set((state) => {
            const existing = state.byUser[userId] ?? [];
            const notification: AppNotification = {
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              title: payload.title,
              description: payload.description,
              link: payload.link,
              category: payload.category,
              isSystemError: payload.isSystemError ?? false,
              systemErrorDetails: payload.systemErrorDetails,
              read: false,
              createdAt: new Date().toISOString(),
            };
            const updated = [notification, ...existing].slice(0, MAX_NOTIFICATIONS);
            return { byUser: { ...state.byUser, [userId]: updated } };
          }),

        markAsRead: (userId, notificationId) =>
          set((state) => {
            const list = (state.byUser[userId] ?? []).map((n) =>
              n.id === notificationId ? { ...n, read: true } : n
            );
            return { byUser: { ...state.byUser, [userId]: list } };
          }),

        markAllAsRead: (userId) =>
          set((state) => {
            const list = (state.byUser[userId] ?? []).map((n) => ({ ...n, read: true }));
            return { byUser: { ...state.byUser, [userId]: list } };
          }),

        removeNotification: (userId, notificationId) =>
          set((state) => {
            const list = (state.byUser[userId] ?? []).filter((n) => n.id !== notificationId);
            return { byUser: { ...state.byUser, [userId]: list } };
          }),

        clearAll: (userId) =>
          set((state) => ({ byUser: { ...state.byUser, [userId]: [] } })),
      },
    }),
    {
      name: 'notification-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ byUser: state.byUser }),
    }
  )
);

// Stable fallback — must not be inline `[]` because `[] !== []` breaks
// React 18's useSyncExternalStore identity check in Zustand v5, causing an
// infinite re-render loop every time the user has no stored notifications.
const EMPTY_NOTIFICATIONS: AppNotification[] = [];

export const useNotificationActions = () =>
  useNotificationStore((state) => state.actions);

export const useUserNotifications = (userId: string | null | undefined) =>
  useNotificationStore((state) =>
    userId ? (state.byUser[userId] ?? EMPTY_NOTIFICATIONS) : EMPTY_NOTIFICATIONS
  );

export const useUnreadCount = (userId: string | null | undefined, isSuperAdmin = false) =>
  useNotificationStore((state) => {
    if (!userId) return 0;
    const list = state.byUser[userId] ?? EMPTY_NOTIFICATIONS;
    return list.filter((n) => !n.read && (isSuperAdmin || !n.isSystemError)).length;
  });
